import type { Commission, CommissionRule, Sale } from '../types'
import { computeSalePaymentSummary } from './salePaymentSummary'

function toNumber(value: unknown): number {
  if (value == null || value === '') return 0
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const n = parseFloat(value.replace(',', '.'))
    return Number.isFinite(n) ? n : 0
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    'toNumber' in value &&
    typeof (value as { toNumber: () => number }).toNumber === 'function'
  ) {
    const n = (value as { toNumber: () => number }).toNumber()
    return Number.isFinite(n) ? n : 0
  }
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function parseTierRulesRaw(
  raw: CommissionRule['tierRules']
): Array<{ min: number; max?: number; percentage: number }> {
  if (raw == null) return []
  if (typeof raw === 'string') {
    try {
      return parseTierRulesRaw(JSON.parse(raw) as CommissionRule['tierRules'])
    } catch {
      return []
    }
  }
  if (!Array.isArray(raw)) return []
  const result: Array<{ min: number; max?: number; percentage: number }> = []
  for (const item of raw) {
    if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
      const typedItem = item as { min?: unknown; max?: unknown; percentage?: unknown }
      result.push({
        min: toNumber(typedItem.min),
        max:
          typedItem.max !== undefined && typedItem.max !== null && String(typedItem.max) !== ''
            ? toNumber(typedItem.max)
            : undefined,
        percentage: toNumber(typedItem.percentage),
      })
    }
  }
  return result
}

function pickTierPercentage(baseValue: number, tierRules: Array<{ min: number; max?: number; percentage: number }>): number {
  if (!tierRules.length) return 0
  const matching = tierRules.find((tier) => {
    const min = tier.min ?? 0
    const max = tier.max ?? Number.POSITIVE_INFINITY
    return baseValue >= min && baseValue < max
  })
  if (matching) return matching.percentage
  const sorted = [...tierRules].sort((a, b) => (a.min ?? 0) - (b.min ?? 0))
  for (let i = sorted.length - 1; i >= 0; i--) {
    const t = sorted[i]
    if (baseValue >= (t.min ?? 0)) return t.percentage
  }
  return 0
}

export function computeCommissionBaseForRule(
  sale: Sale,
  calculationBase: CommissionRule['calculationBase'] | undefined
): number {
  const final = toNumber(sale.finalValue)
  const baseKind = calculationBase || 'vehicle_value'
  if (baseKind === 'profit') {
    const paymentSummary = computeSalePaymentSummary(sale)
    return toNumber(paymentSummary.totalConfirmed) - final
  }
  if (baseKind === 'financed_value') {
    const payments = sale.proposal?.payments ?? []
    let sum = 0
    for (const p of payments) {
      const name = p.paymentForm?.name?.toLowerCase() ?? ''
      if (name.includes('financiamento')) {
        const instSum =
          p.installmentPayments?.reduce((s, inst) => s + toNumber(inst.value), 0) ?? 0
        sum += instSum
      }
    }
    return sum > 0 ? sum : final
  }
  return final
}

export function computeCommissionAmountsFromRule(
  rule: CommissionRule,
  baseValue: number
): { percentage: number; calculatedValue: number } {
  let percentage = 0
  let fixedAmount = 0
  let calculatedValue = 0

  const commissionType = rule.commissionType || 'percentage'
  const useTiers = rule.type === 'tier' || parseTierRulesRaw(rule.tierRules).length > 0

  if (commissionType === 'percentage' || commissionType === 'mixed') {
    if (useTiers) {
      const tiers = parseTierRulesRaw(rule.tierRules)
      percentage = pickTierPercentage(baseValue, tiers)
    } else {
      percentage = toNumber(rule.percentageValue)
    }
  }

  if (commissionType === 'fixed' || commissionType === 'mixed') {
    fixedAmount = toNumber(rule.fixedValue)
  }

  if (commissionType === 'percentage') {
    calculatedValue = (baseValue * percentage) / 100
  } else if (commissionType === 'fixed') {
    calculatedValue = fixedAmount
  } else if (commissionType === 'mixed') {
    calculatedValue = (baseValue * percentage) / 100 + fixedAmount
  }

  const maxCap = toNumber(rule.maxCommission)
  if (maxCap > 0 && calculatedValue > maxCap) {
    calculatedValue = maxCap
  }

  return { percentage, calculatedValue }
}

function shouldRecomputeFromRule(
  rule: CommissionRule | null | undefined,
  storedPct: number,
  storedVal: number
): boolean {
  if (!rule) return false
  if (rule.isMandatory) return true
  return storedPct === 0 && storedVal === 0
}

export function resolveRuleCalculatedAmount(
  commission: Commission,
  sale?: Sale | null,
  fallbackRule?: CommissionRule | null
): number {
  const rule = commission.rule ?? fallbackRule ?? null
  const saleForCalc = sale ?? commission.sale ?? null
  if (!rule || !saleForCalc) return 0
  const base = computeCommissionBaseForRule(saleForCalc, rule.calculationBase)
  return computeCommissionAmountsFromRule(rule, base).calculatedValue
}

export function commissionOriginalPaidAndDiff(
  commission: Commission,
  sale?: Sale | null,
  fallbackRule?: CommissionRule | null
): {
  original: number
  paid: number
  diff: number
} {
  const display = resolveCommissionDisplay(commission, sale, fallbackRule)
  const storedPct = toNumber(commission.percentage)
  const storedVal = toNumber(commission.calculatedValue)
  const rule = commission.rule ?? fallbackRule ?? null
  const saleForCalc = sale ?? commission.sale ?? null
  const needsClientCompute =
    Boolean(rule) &&
    Boolean(saleForCalc) &&
    shouldRecomputeFromRule(rule, storedPct, storedVal)

  const agreed = needsClientCompute ? display.calculatedValue : storedVal

  const origRaw = commission.originalCalculatedValue
  const hasOrig = origRaw !== undefined && origRaw !== null && origRaw !== ''
  const ruleOriginal = resolveRuleCalculatedAmount(commission, sale, fallbackRule)
  const original =
    rule && saleForCalc
      ? ruleOriginal
      : hasOrig
        ? toNumber(origRaw)
        : storedVal

  return { original, paid: agreed, diff: agreed - original }
}

export function resolveCommissionDisplay(
  commission: Commission,
  sale?: Sale | null,
  fallbackRule?: CommissionRule | null
): { baseValue: number; percentage: number; calculatedValue: number } {
  const storedBase = toNumber(commission.baseValue)
  const storedPct = toNumber(commission.percentage)
  const storedVal = toNumber(commission.calculatedValue)
  const rule = commission.rule ?? fallbackRule ?? null
  const saleForCalc = sale ?? commission.sale ?? null

  if (!rule || !saleForCalc || !shouldRecomputeFromRule(rule, storedPct, storedVal)) {
    return {
      baseValue: storedBase,
      percentage: storedPct,
      calculatedValue: storedVal,
    }
  }

  const base = computeCommissionBaseForRule(saleForCalc, rule.calculationBase)
  const { percentage, calculatedValue } = computeCommissionAmountsFromRule(rule, base)
  return { baseValue: base, percentage, calculatedValue }
}

export function commissionRuleHasFixedPortion(rule: CommissionRule | null | undefined): boolean {
  if (!rule) return false
  return rule.commissionType === 'fixed' || rule.commissionType === 'mixed'
}

export function commissionRuleFixedAmount(rule: CommissionRule | null | undefined): number {
  if (!commissionRuleHasFixedPortion(rule)) return 0
  return toNumber(rule?.fixedValue)
}

export function sumCommissionBonuses(commission: Commission): number {
  return (commission.bonuses ?? []).reduce((s, b) => s + toNumber(b.amount), 0)
}
